/**
 * Modal
 * Created by calhosh on 6/5/2020.
 */
import React from 'react';
import styled, {ThemeProvider} from 'styled-components';
import classNames from 'classnames';
import PropTypes from 'prop-types';

import {labels} from '../../../util/utils.js';

import theme from '@ey/styled-theme';

import MotifModal from '@ey/motif-react-wrapper/Modal';
import MotifModalBody from '@ey/motif-react-wrapper/ModalBody';
import MotifModalHeader from '@ey/motif-react-wrapper/ModalHeader';
import MotifModalFooter from '@ey/motif-react-wrapper/ModalFooter';

import MotifIconButton from '@ey/motif-react-wrapper/IconButton';
import MotifIcon from '@ey/motif-react-wrapper/Icon';
import {navigationIcArrowBack24px} from '@ey/motif-react-wrapper/assets/icons/index.js';
import {SVGICONS} from '@ey/icons';

import Button from '../../CoreWrappers/Button';

function Modal(props) {
	const {
		show,
		onHide,
		onOkClick,
		onSaveCreateClick,
		title,
		children,
		isOkButtonDisabled,
		isCancelButtonDisabled,
		isSaveCreateButtonDisabled,
		showButtons,
		hideCloseButton,
		hideConfirmButton,
		hideCancelButton,
		confirmBtnTitle,
		closeBtnTitle,
		saveCreateBtnTitle,
		closeTitle,
		modalsize,
		modalbodystyle,
		modalContainerClass,
		modalonmodal,
		disableClose,
		footerInfo,
		backButton,
		onCancel
	} = props;

	const renderBackButton = () => {
		const props = {
			type: 'button',
			onClick: backButton.onClick,
			className: 'motif-modal--header-icon-button'
		};

		return (
			<MotifIconButton {...props}>
				<MotifIcon src={navigationIcArrowBack24px} title={backButton.title || labels.back} />
			</MotifIconButton>
		);
	};

	return (
		<StyledMotifModal
			show={show}
			onClose={onHide}
			className={modalsize + ' ' + modalContainerClass}
			modalonmodal={modalonmodal}
		>
			<ThemeProvider theme={theme}>
				<StyledModalHeader
					className={classNames({['with-back-btn']: backButton !== null})}
					visibility={hideCloseButton}
					closeModalButton={
						<MotifIconButton
							type="button"
							onClick={onHide}
							className="motif-modal--header-icon-button"
							title={closeTitle}
							disabled={disableClose}
						>
							<SVGICONS
								styleName="medium"
								name="close-outline"
								hoverText={closeTitle}
								defaultIcon=""
								onIconClick={() => {}}
							/>
						</MotifIconButton>
					}
				>
					{backButton?.onClick && renderBackButton()}
					{title}
				</StyledModalHeader>
			</ThemeProvider>
			<StyledModalBody className={modalbodystyle}>{children}</StyledModalBody>
			{showButtons && (
				<StyledModalFooter>
					{!hideConfirmButton && (
						<Button
							type="button"
							buttonTitle={confirmBtnTitle}
							label={confirmBtnTitle}
							onClick={onOkClick}
							disabled={isOkButtonDisabled}
						/>
					)}
					{saveCreateBtnTitle && (
						<Button
							type="button"
							variant="secondary"
							disabled={isSaveCreateButtonDisabled}
							onClick={onSaveCreateClick}
							label={saveCreateBtnTitle}
							buttonTitle={saveCreateBtnTitle}
						/>
					)}
					{!hideCancelButton && (
						<Button
							label={closeBtnTitle}
							buttonTitle={closeBtnTitle}
							type="button"
							variant="secondary"
							disabled={isCancelButtonDisabled}
							onClick={() => {
								if (onCancel) onCancel();
								else onHide();
							}}
						/>
					)}
					{footerInfo && <section className="modal-footer-info">{footerInfo}</section>}
				</StyledModalFooter>
			)}
		</StyledMotifModal>
	);
}

Modal.propTypes = {
	children: PropTypes.oneOfType([PropTypes.object, PropTypes.array]),
	title: PropTypes.oneOfType([PropTypes.string, PropTypes.element]),
	modalsize: PropTypes.string,
	confirmBtnTitle: PropTypes.string,
	closeBtnTitle: PropTypes.string,
	saveCreateBtnTitle: PropTypes.string,
	modalbodystyle: PropTypes.string,
	modalContainerClass: PropTypes.string,
	showButtons: PropTypes.bool,
	hideCloseButton: PropTypes.bool,
	hideConfirmButton: PropTypes.bool,
	hideCancelButton: PropTypes.bool,
	isOkButtonDisabled: PropTypes.bool,
	isCancelButtonDisabled: PropTypes.bool,
	isSaveCreateButtonDisabled: PropTypes.bool,
	disabled: PropTypes.bool,
	show: PropTypes.bool,
	onCancel: PropTypes.func,
	backButton: PropTypes.shape({
		title: PropTypes.string,
		onClick: PropTypes.func
	})
};

Modal.defaultProps = {
	backButton: null,
	onCancel: null,
	show: false,
	closeBtnTitle: '',
	saveCreateBtnTitle: '',
	modalbodystyle: '',
	modalContainerClass: '',
	hideCloseButton: false,
	hideConfirmButton: false,
	hideCancelButton: false,
	isOkButtonDisabled: false,
	isCancelButtonDisabled: false,
	isSaveCreateButtonDisabled: false,
	disabled: false
};

export default Modal;

const StyledMotifModal = styled(MotifModal)`
	-webkit-border-radius: 0.8rem;
	-moz-border-radius: 0.8rem;
	border-radius: 0.8rem;

	.motif-modal-overlay {
		padding: 1rem;
	}
	&.motif-modal-content {
		width: 100%;
		max-width: 100%;
		height: 100%;
		max-height: 100%;

		&.small {
			width: 50%;
			max-width: 50%;
			height: auto;
			max-height: auto;
		}

		&.medium {
			width: 72%;
			max-width: 72%;
			height: calc(100vh - 4rem);
			max-height: auto;
		}

		&.message {
			width: 30%;
			max-width: 30%;
			height: auto;
			max-height: auto;
		}
	}

	.motif-modal-body {
		padding: 1.2rem 2rem;
		&.no-padding {
			padding: 0;
		}
		position: relative;
		font-size: 1.2rem;
	}
`;

const StyledModalHeader = styled(MotifModalHeader)`
	&.motif-modal-header {
		padding: 1.2rem 2rem;
		height: auto;

		.motif-modal-headline {
			font-weight: bold;
			font-size: 1.7rem;
			padding: 0;
		}
		.motif-modal--header-icon-button {
			${({visibility}) => visibility && `visibility: hidden;`};
			box-shadow: none;
			width: 2.4rem;
			height: 2.4rem;
			margin-top: -0.3rem;
			&:hover,
			&:focus,
			&:active {
				background: none;
				svg {
					background-color: transparent;
					box-shadow: none;
				}
			}
			svg {
				width: 2.4rem;
				height: 2.4rem;
			}
		}

		&.with-back-btn {
			.motif-modal--header-icon-button {
				margin: 0;
			}
			.motif-modal-headline {
				display: flex;
				gap: 2rem;
				align-items: center;
				.motif-modal--header-icon-button {
					--size: 2.4rem;
					width: var(--size);
					height: var(--size);
					.motif-icon {
						width: var(--size);
						height: var(--size);
					}
				}
			}
		}
	}
`;

const StyledModalBody = styled(MotifModalBody)`
	&.motif-modal-body {
		overflow: hidden;
		height: inherit;
		display: flex;
		flex-direction: column;
		&.modalMinHeight {
			min-height: 20rem;
		}
		.errorMessageWrapper {
			z-index: 10;
		}
		.motif-progress-loader.motif-progress-loader-container {
			z-index: 20;
		}

		&.customScrollbar {
			overflow-y: auto;
		}

		.full-height {
			height: 100%;
		}
	}
`;

const StyledModalFooter = styled(MotifModalFooter)`
	&.motif-modal-footer {
		padding: 1rem 2rem;
		button {
			font-size: 1.4rem;
			padding: 0.7rem 3rem;
			min-width: 8.8rem;
		}
	}
	.modal-footer-info {
		display: flex;
		align-items: center;
		margin-left: 2rem;
	}

	.motif-button {
		border-radius: 0.4rem;
	}
`;
